import React, { memo } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function InvestorOverviewPage() {
  return <div className="min-h-screen bg-gray-50 font-sans text-[#111111]" data-id="element-2802">
      {/* Private Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-2803">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-2804">
          <div className="flex items-center space-x-4" data-id="element-2805">
            <Link to="/investor-portal" className="text-gray-500 hover:text-[#631bb7] transition-colors" data-id="element-2806">
              <ArrowLeftIcon size={20} data-id="element-2807" />
            </Link>
            <div className="h-6 w-px bg-gray-200" data-id="element-2808"></div>
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-6" data-id="element-2809" />
            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wide" data-id="element-2810">
              Investor Portal
            </span>
          </div>
          <div className="text-sm text-gray-500" data-id="element-2811">
            Confidential & Proprietary
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-12" data-id="element-2812">
        <div className="space-y-4" data-id="element-2813">
          <h1 className="text-4xl font-bold text-[#111111]" data-id="element-2814">
            Solution Overview
          </h1>
          <p className="text-xl text-gray-500" data-id="element-2815">
            Our platform, philosophy, and how we serve families.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-200 shadow-sm space-y-10" data-id="element-2816">
          {/* 1. The Problem We're Solving */}
          <section data-id="element-2817">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-2818">
              The Problem We're Solving
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-2819">
              <p data-id="element-2820">
                Most consumer-focused digital security products aren't
                appropriate for the average person. They're overly technical,
                using terminology that only someone with cybersecurity
                experience would understand. They're often difficult to set up,
                and using them effectively requires expertise most families
                simply don't have.
              </p>
              <p data-id="element-2821">
                Worse, the market is dominated by narrow point solutions that
                each address a single issue in isolation: VPNs, password
                managers, data removal tools. Each solves a small slice of the
                problem, but none reflect how real people actually experience
                technology in their daily lives. The result is a false sense of
                security, when in reality most individuals and families could
                dramatically reduce their risk by doing a handful of basic
                things consistently and doing them well.
              </p>
              <p data-id="element-2822">
                Consumer cybersecurity offerings also haven't kept pace with how
                families use technology today. Modern households are
                multi-device, multi-generation, and constantly changing. Yet
                current solutions fall into two flawed camps:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2823">
                <li data-id="element-2824">
                  Highly technical, a-la-carte tools marketed to power users;
                  they require setup, expertise, and ongoing attention to work
                  correctly.
                </li>
                <li data-id="element-2825">
                  One-size-fits-all security suites that promise simplicity but
                  lack personalization; they're often purchased with good
                  intentions, then ignored. Classic shelfware.
                </li>
              </ul>
              <p data-id="element-2826">
                Both approaches fail for the same reason: they ignore the human
                side of security. Safe digital behavior isn't just a product
                problem. It's an adoption, education, and enablement problem.
                Without guidance, reinforcement, and training designed around
                real people, even the best tools go unused or misused. The
                missing ingredient isn't more software. It's a bespoke,
                human-centered model that helps people understand what to do,
                why it matters, and how to keep doing it over time.
              </p>
            </div>
          </section>

          {/* 2. The Core Philosophy */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2827">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-2828">
              The Core Philosophy
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-2829">
              <p data-id="element-2830">
                Caveo operates on a fundamental shift in perspective: digital
                safety is an education problem, not a software problem. We do
                not sell fear, reactive monitoring, or insurance. Instead, we
                serve as a trusted stewardship layer, systematically building
                digital capability while consolidating and securing technology
                stacks into a manageable, cost-effective ecosystem.
              </p>
              <p data-id="element-2831">
                Unlike competitors who profit from fear and dependency, Caveo
                succeeds when people become self-sufficient. Our business model
                aligns with customer outcomes: the better we enable them, the
                more they trust us with their entire digital ecosystem.
              </p>
              <p data-id="element-2832">
                Caveo is the first platform developed specifically to safeguard
                individuals and families against online scams and help them
                leverage technology to improve their quality of life. Our
                approach draws inspiration from enterprise IT governance and
                ongoing corporate cybersecurity training, but built specifically
                for outside the workplace and made genuinely simple.
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2833">
                <li data-id="element-2834">
                  We leverage learning science and personalization to meet
                  consumers where they are in their journey while providing them
                  the means to learn at their own pace.
                </li>
                <li data-id="element-2835">
                  We use engaging, bite-sized lessons inspired by modern content
                  consumption habits.
                </li>
                <li data-id="element-2836">
                  We integrate proven learning methods to make learning
                  engaging, not punishing.
                </li>
                <li data-id="element-2837">
                  We apply principles of change management, guiding users step
                  by step so changes actually stick.
                </li>
              </ul>
              <p className="font-semibold text-[#111111] mt-4" data-id="element-2838">
                The result: a system that not only protects but empowers.
              </p>
            </div>
          </section>

          {/* 3. The Home IT Department Framework */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2839">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-2840">
              The Home IT Department Framework
            </h2>
            <div className="space-y-6 text-gray-600 leading-relaxed" data-id="element-2841">
              <p data-id="element-2842">
                Every household that functions smoothly in the digital age has
                the same system working behind the scenes, whether they realize
                it or not. Someone is playing the role of IT: setting up the
                router, resetting passwords at 11 p.m., explaining to Grandma
                why her email is locked, and ensuring a lapsed cloud storage
                subscription doesn't take the family photos with it. That role
                exists. It's simply invisible, unstructured, and usually falling
                on one exhausted family member.
              </p>
              <p data-id="element-2843">
                That's where Caveo comes in. What enterprise IT departments do
                for multi-million dollar companies, Caveo does for the homes
                those employees go home to. The parallels are exact. Enterprise
                IT covers four domains: infrastructure, security, productivity,
                and governance. So does Caveo, translated for real life.
              </p>

              <div className="mt-8 space-y-8" data-id="element-2844">
                <div data-id="element-2845">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2846">
                    Infrastructure
                  </h3>
                  <p data-id="element-2847">
                    Infrastructure is everything that must function before
                    anything else can happen. It's the hardware and software
                    that powers your home Wi-Fi network, the fleet of devices
                    spread across three generations, and the cloud storage
                    silently protecting memories in the background. When it
                    works, it's invisible. When it doesn't, the family tech
                    person gets a call. Caveo maps the full household ecosystem,
                    identifies gaps, and builds a foundation that is reliable
                    across every device the family actually uses.
                  </p>
                  <p className="mt-2" data-id="element-2848">
                    This includes secure Wi-Fi setup with strong encryption,
                    updated router firmware, and secure admin passwords. It
                    includes guest network creation to isolate visitors and
                    non-essential devices from the family's core systems. And it
                    includes smart home integration: once the network is
                    segmented, families can safely connect smart devices like
                    cameras, TVs, and doorbells. Caveo guides them through
                    ensuring firmware is updated, setting unique passwords,
                    disabling risky or unnecessary features, and monitoring
                    device health and access over time.
                  </p>
                </div>

                <div data-id="element-2849">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2850">
                    Security
                  </h3>
                  <p data-id="element-2851">
                    Security is where the stakes become real. Corporate IT
                    protects against phishing and ransomware; families face
                    those same threats with far fewer defenses.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 my-4" data-id="element-2852">
                    <p className="font-medium text-[#111111]" data-id="element-2853">
                      The FBI's 2024 Internet Crime Report (IC3) documented{' '}
                      <span className="font-bold text-[#631bb7]" data-id="element-2854">
                        $16.6 billion
                      </span>{' '}
                      in losses from cybercrime, with seniors being the most
                      targeted demographic, losing an average of{' '}
                      <span className="font-bold text-[#631bb7]" data-id="element-2855">$83,000</span>.
                    </p>
                  </div>
                  <p data-id="element-2856">
                    Without a security function, a family is one convincing
                    phone call away from an irreversible wire transfer. Caveo
                    provides that function: MFA setup, password hygiene, scam
                    recognition, and device hardening. Most threats are stopped
                    quietly before a family member even encounters them. Perhaps
                    most importantly, we help bridge the gap between a user's
                    current state and the adoption of a password manager. That
                    bridge is often learning how and why to create unique
                    passwords, building a system of record, and developing the
                    confidence to make the leap. Device-level security includes
                    privacy and security settings tuned for each device type,
                    automatic updates and patch management, and endpoint
                    protections that scale across phones, laptops, and tablets.
                  </p>
                </div>

                <div data-id="element-2857">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2858">
                    Productivity
                  </h3>
                  <p data-id="element-2859">
                    Productivity in the enterprise reduces friction; in a
                    household, it reduces the support tax paid by the tech-savvy
                    family member. Research shows the average tech-literate
                    adult spends{' '}
                    <span className="font-bold text-[#111111]" data-id="element-2860">
                      35 hours a month
                    </span>{' '}
                    on family tech support, nearly a full work week of
                    compounding, repetitive tasks. Caveo standardizes tools,
                    automates maintenance, and trains family members both at
                    their own pace and in ways that mean the same questions stop
                    coming back. The driver of productivity is measured by time
                    and money saved by being proactive when it comes to online
                    security.
                  </p>
                </div>

                <div data-id="element-2861">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2862">
                    Governance
                  </h3>
                  <p data-id="element-2863">
                    Governance is the piece no one talks about until something
                    goes wrong. In a company, it's compliance and audits. In a
                    household, it's knowing which subscriptions have been
                    quietly billing for 18 months, who has access to which
                    accounts, and what happens to digital assets when someone
                    passes. Caveo is the governance layer, the system of record
                    for the family's entire digital life. The best example of
                    governance shows up in terms of standardization of operating
                    systems and devices. For example, if the entire family can
                    eventually migrate to exclusively using Apple products,
                    offerings such as iCloud and HomeKit become the default and
                    sharing Family Subscriptions instantly allows for cost
                    savings.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 4. The Business Case, Translated */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2864">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-2865">
              The Business Case, Translated
            </h2>
            <ul className="space-y-4 text-gray-600 leading-relaxed" data-id="element-2866">
              <li data-id="element-2867">
                <span className="font-bold text-[#111111]" data-id="element-2868">Cost Control:</span>{' '}
                The average U.S. household wastes over $300 per year on
                redundant subscriptions. Caveo audits and consolidates that
                spend, eliminating shelfware.
              </li>
              <li data-id="element-2869">
                <span className="font-bold text-[#111111]" data-id="element-2870">
                  Risk Reduction:
                </span>{' '}
                Identity theft costs a family an average of $1,200 in direct
                losses, plus the massive toll of credit recovery. IT reduces
                exposure before the incident.
              </li>
              <li data-id="element-2871">
                <span className="font-bold text-[#111111]" data-id="element-2872">
                  Operational Continuity:
                </span>{' '}
                When a device fails or an account is compromised, Caveo ensures
                a backup plan is already in place. Resilience is engineered, not
                improvised.
              </li>
              <li data-id="element-2873">
                <span className="font-bold text-[#111111]" data-id="element-2874">
                  Family Experience:
                </span>{' '}
                From onboarding an aging parent to a password manager to setting
                up new devices, Caveo provides a guided, role-appropriate
                experience.
              </li>
              <li data-id="element-2875">
                <span className="font-bold text-[#111111]" data-id="element-2876">Scalability:</span> A
                two-person household can manage informally. Add teenagers, aging
                parents, and digital estate planning, and informal stops
                working.
              </li>
              <li data-id="element-2877">
                <span className="font-bold text-[#111111]" data-id="element-2878">
                  Strategic Enablement:
                </span>{' '}
                The best IT teams unlock new capabilities. Caveo's AI guidance
                layer proactively identifies vulnerabilities and builds
                household digital fluency over time.
              </li>
            </ul>
          </section>

          {/* 5. The Shared Digital Reality */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2879">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-2880">
              The Shared Digital Reality
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-2881">
              <p data-id="element-2882">
                Without a central system, organizations drift into tool chaos,
                where integration is folklore and security is wishful thinking.
                Families face the same drift: Dad uses Chrome, Mom uses Safari,
                and Grandma is on an outdated browser, all while sharing a
                Netflix password via a sticky note. No one knows who has access
                or what to do when something breaks.
              </p>
              <p data-id="element-2883">
                Caveo creates the household equivalent of a shared digital
                reality. Not through surveillance, but through structure,
                education, and a system that every family member can actually
                use. The enterprise IT department took decades to become a
                standard organizational function. Today, we take it for granted.
                The household equivalent doesn't exist yet. That is exactly what
                Caveo is building.
              </p>
            </div>
          </section>

          {/* 6. How It Works: The Customer Journey */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2884">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-2885">
              How It Works: The Customer Journey
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-2886">
              Caveo's onboarding is deliberately staged to deliver immediate
              value before asking for significant time investment. The
              experience is designed so that every step earns the next one.
            </p>

            <div className="space-y-12" data-id="element-2887">
              {/* Step 1 */}
              <div data-id="element-2888">
                <h3 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2889">
                  Step 1: Onboarding — The Initial Assessment (3-4 Minutes)
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-2890">
                  Every Caveo journey begins with immediate value delivery
                  before users even complete their first login. This two-stage
                  approach reduces abandonment while delivering value within
                  minutes.
                </p>
                <div className="space-y-4 text-gray-600" data-id="element-2891">
                  <div data-id="element-2892">
                    <h4 className="font-bold text-[#111111]" data-id="element-2893">
                      Quick Discovery
                    </h4>
                    <p data-id="element-2894">Users provide basic information to get started:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-2895">
                      <li data-id="element-2896">Account creation and family structure</li>
                      <li data-id="element-2897">Immediate pain points and frustrations</li>
                      <li data-id="element-2898">High-level device inventory</li>
                      <li data-id="element-2899">Primary security concerns</li>
                    </ul>
                  </div>
                  <div data-id="element-2900">
                    <h4 className="font-bold text-[#111111]" data-id="element-2901">Instant Value</h4>
                    <p data-id="element-2902">
                      Based on these initial responses, users receive immediate
                      recommendations and quick wins they can implement right
                      away. We provide 2-3 actionable tips before they've
                      invested significant time, creating trust and momentum.
                    </p>
                  </div>
                  <div data-id="element-2903">
                    <h4 className="font-bold text-[#111111]" data-id="element-2904">
                      The Return Hook
                    </h4>
                    <p data-id="element-2905">
                      Users are invited to return for the comprehensive
                      assessment that unlocks their full personalized curriculum
                      and shopping guide. We earn your investment before we ask
                      for your information.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div data-id="element-2906">
                <h3 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2907">
                  Step 2: Foundation Setting — The Introduction Module
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-2908">
                  Before diving into tactical security topics, every user
                  completes the foundational Introduction Module: Understanding
                  Digital Security and Privacy Trade-offs. This establishes the
                  framework for all future learning. This isn't a lecture. It's
                  a conversation about values and realistic expectations.
                </p>
                <div className="space-y-4 text-gray-600" data-id="element-2909">
                  <h4 className="font-bold text-[#111111]" data-id="element-2910">What Users Learn</h4>
                  <ul className="space-y-3" data-id="element-2911">
                    <li data-id="element-2912">
                      <span className="font-bold text-[#111111]" data-id="element-2913">
                        What 'Free' Really Costs:
                      </span>{' '}
                      How advertising-based business models work. What data
                      companies collect from free services. Why Gmail doesn't
                      charge money but does collect information for targeted
                      advertising.
                    </li>
                    <li data-id="element-2914">
                      <span className="font-bold text-[#111111]" data-id="element-2915">
                        The Privacy-Convenience Spectrum:
                      </span>{' '}
                      Not everyone needs maximum privacy. Not everyone wants
                      maximum convenience. Understanding where you fall on this
                      spectrum helps you make informed choices about which
                      trade-offs are acceptable for your family.
                    </li>
                    <li data-id="element-2916">
                      <span className="font-bold text-[#111111]" data-id="element-2917">
                        Risk Tolerance Assessment:
                      </span>{' '}
                      What are you trying to protect against? Random hackers?
                      Advertisers? Government surveillance? Your actual threat
                      model determines which security measures matter most.
                    </li>
                    <li data-id="element-2918">
                      <span className="font-bold text-[#111111]" data-id="element-2919">
                        Setting Family Security Goals:
                      </span>{' '}
                      Based on your values around privacy vs. convenience and
                      cost vs. features, what does good enough security look
                      like for your family? This isn't about achieving
                      perfection. It's about achieving a level of protection
                      that matches your actual needs and lifestyle.
                    </li>
                  </ul>
                  <div className="mt-4" data-id="element-2920">
                    <h4 className="font-bold text-[#111111]" data-id="element-2921">Outcome</h4>
                    <p data-id="element-2922">
                      Users complete this module with shared vocabulary and
                      realistic expectations. They understand that security
                      isn't one-size-fits-all, that trade-offs are inevitable,
                      and that their family's approach should be based on their
                      specific situation rather than generic fear-based advice.
                      This foundation ensures everyone starts from the same
                      baseline before tackling practical security modules.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div data-id="element-2923">
                <h3 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2924">
                  Step 3: Deep Discovery — The Full Assessment (10 Minutes)
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-2925">
                  Upon returning to the platform, users complete the
                  comprehensive assessment that unlocks their personalized
                  curriculum and shopping guide.
                </p>
                <div className="space-y-6 text-gray-600" data-id="element-2926">
                  <div data-id="element-2927">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2928">
                      What We Map
                    </h4>
                    <ul className="space-y-2" data-id="element-2929">
                      <li data-id="element-2930">
                        <span className="font-bold text-[#111111]" data-id="element-2931">
                          Household Topology:
                        </span>{' '}
                        Who lives where, who manages WiFi in each home, which
                        households share networks vs. operate independently. For
                        multi-household families such as adult children helping
                        parents who live separately, this creates the
                        coordination structure.
                      </li>
                      <li data-id="element-2932">
                        <span className="font-bold text-[#111111]" data-id="element-2933">
                          Complete Device Inventory:
                        </span>{' '}
                        Every phone, tablet, computer, TV, streaming device,
                        smart home gadget, router, and everything else that
                        connects to the internet. For tech-savvy users, we offer
                        an optional network scan that automatically detects
                        connected devices, accelerating this process.
                      </li>
                      <li data-id="element-2934">
                        <span className="font-bold text-[#111111]" data-id="element-2935">
                          Services and Subscriptions:
                        </span>{' '}
                        Cloud storage, streaming services, security software,
                        VPNs: what they're paying for and what they're actually
                        using. This is where we uncover Wallet Leakage.
                      </li>
                      <li data-id="element-2936">
                        <span className="font-bold text-[#111111]" data-id="element-2937">
                          Family Relationships:
                        </span>{' '}
                        Who helps whom with tech, who's tech-savvy vs.
                        tech-anxious, whose devices need to be compatible for
                        remote support. This informs ecosystem recommendations.
                      </li>
                      <li data-id="element-2938">
                        <span className="font-bold text-[#111111]" data-id="element-2939">
                          Privacy Tolerance:
                        </span>{' '}
                        Where users fall on the spectrum from maximum
                        convenience to maximum privacy. This personalizes
                        recommendations: some families want Google Assistant
                        everywhere, others want minimal data collection.
                      </li>
                      <li data-id="element-2940">
                        <span className="font-bold text-[#111111]" data-id="element-2941">
                          Current Practices:
                        </span>{' '}
                        How they handle passwords, whether they backup devices,
                        what they do when something goes wrong. This reveals
                        immediate vulnerabilities.
                      </li>
                    </ul>
                  </div>

                  <div data-id="element-2942">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2943">
                      Transparency Principle
                    </h4>
                    <p data-id="element-2944">
                      Users can skip questions or defer answers, but the
                      assessment is honest about what's needed for good
                      recommendations. We can't help you consolidate
                      subscriptions if you don't tell us what you're paying for.
                    </p>
                  </div>

                  <div data-id="element-2945">
                    <h4 className="font-bold text-[#111111] mb-3" data-id="element-2946">
                      What the Assessment Reveals — Immediate ROI
                    </h4>
                    <p className="mb-4" data-id="element-2947">
                      The assessment isn't judgmental. It's diagnostic. We show
                      families what exists, what's working, what's not, and
                      where the opportunities for improvement are. Examples of
                      what we surface:
                    </p>
                    <div className="space-y-3" data-id="element-2948">
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2949">
                        <span className="font-bold text-[#111111]" data-id="element-2950">
                          Shadow Subscriptions:
                        </span>{' '}
                        <span className="italic" data-id="element-2951">
                          "You're paying for three different cloud storage
                          services totaling $155/year. You could save $115
                          annually by switching to one family plan."
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2952">
                        <span className="font-bold text-[#111111]" data-id="element-2953">
                          Compatibility Gaps:
                        </span>{' '}
                        <span className="italic" data-id="element-2954">
                          "Your mom uses an iPhone but has a Windows laptop.
                          When that laptop dies, she should replace it with
                          something that syncs with her iPhone so file sharing
                          actually works."
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2955">
                        <span className="font-bold text-[#111111]" data-id="element-2956">
                          Security Holes:
                        </span>{' '}
                        <span className="italic" data-id="element-2957">
                          "You have two-factor authentication enabled on your
                          bank account but not on your email. If someone
                          compromises your email, they can reset your bank
                          password. Let's fix that."
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2958">
                        <span className="font-bold text-[#111111]" data-id="element-2959">
                          Ecosystem Mismatches:
                        </span>{' '}
                        <span className="italic" data-id="element-2960">
                          "You have Alexa speakers but your family is
                          Apple-centric. Your smart home devices should work
                          with HomeKit for better integration."
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-2961">
                        <span className="font-bold text-[#111111]" data-id="element-2962">
                          Wallet Leakage:
                        </span>{' '}
                        <span className="italic" data-id="element-2963">
                          "You're paying for Norton antivirus on your Mac. MacOS
                          has built-in protection (XProtect) that's sufficient
                          for most users. You could save $100/year by canceling
                          Norton and using what's already included."
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-2964">
                    <div className="bg-red-50 p-6 rounded-xl border border-red-100" data-id="element-2965">
                      <h4 className="font-bold text-red-800 mb-2" data-id="element-2966">
                        Before Caveo
                      </h4>
                      <p className="text-red-900" data-id="element-2967">
                        7 separate subscriptions, $900 per year, zero
                        coordination, massive compatibility problems.
                      </p>
                    </div>
                    <div className="bg-green-50 p-6 rounded-xl border border-green-100" data-id="element-2968">
                      <h4 className="font-bold text-green-800 mb-2" data-id="element-2969">
                        After Caveo
                      </h4>
                      <p className="text-green-900" data-id="element-2970">
                        3 unified family plans plus Caveo at $5.99 per user per
                        month, total annual cost approximately $450, full
                        visibility across the household, standardized ecosystem.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div data-id="element-2971">
                <h3 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2972">
                  Step 4: On the Platform — Four Core Features
                </h3>
                <p className="text-gray-600 mb-8" data-id="element-2973">
                  Once onboarding is complete, users access the full platform,
                  which is built around four interconnected features: capability
                  building, security governance activation, ongoing value
                  through the Living Tech Roadmap and AI support, and family
                  coordination through the dashboard.
                </p>

                <div className="space-y-10" data-id="element-2974">
                  {/* Feature 1 */}
                  <div data-id="element-2975">
                    <h4 className="text-lg font-bold text-[#631bb7] mb-3" data-id="element-2976">
                      Feature 1: Building Capability — The Education Framework
                    </h4>
                    <p className="text-gray-600 mb-4" data-id="element-2977">
                      Based on assessment results and Introduction Module
                      completion, each family member receives a personalized
                      curriculum with modules sequenced by priority. Modules are
                      organized into three levels plus a set of specialized
                      guides, with the sequencing designed so that the
                      highest-risk vulnerabilities are addressed first.
                    </p>

                    <div className="space-y-6 text-gray-600" data-id="element-2978">
                      <div data-id="element-2979">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-2980">
                          Level 1: Essential Digital Safety (9 Modules)
                        </h5>
                        <p className="mb-2 text-sm" data-id="element-2981">
                          If a family member only has energy to complete one
                          tier, this is it. Level 1 addresses the
                          vulnerabilities that cause 90% of security incidents.
                          Completion means you've closed the biggest security
                          holes and are no longer an easy target.
                        </p>
                        <ul className="list-disc pl-6 space-y-1" data-id="element-2982">
                          <li data-id="element-2983">Creating Bulletproof Passwords</li>
                          <li data-id="element-2984">Two-Factor Authentication Basics</li>
                          <li data-id="element-2985">Securing Your Home Wi-Fi Network</li>
                          <li data-id="element-2986">Securing Your Phone</li>
                          <li data-id="element-2987">Securing Your Computer</li>
                          <li data-id="element-2988">Securing Your Most Important Accounts</li>
                          <li data-id="element-2989">Recognizing Scams and Phishing</li>
                          <li data-id="element-2990">Software Updates and Why They Matter</li>
                          <li data-id="element-2991">Backing Up Your Devices</li>
                        </ul>
                      </div>

                      <div data-id="element-2992">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-2993">
                          Level 2: Intermediate Protection (11 Modules)
                        </h5>
                        <p className="mb-2 text-sm" data-id="element-2994">
                          These modules complete the security foundation.
                          Cybersecurity experts would look at Level 1 and Level
                          2 completion and say you're good to go. Level 1 +
                          Level 2 completion equals a 1000/1000 Security Score:
                          you are legitimately secure according to cybersecurity
                          best practices.
                        </p>
                        <ul className="list-disc pl-6 space-y-1" data-id="element-2995">
                          <li data-id="element-2996">Encrypting Your Devices</li>
                          <li data-id="element-2997">
                            Password Management (transitioning to password
                            managers)
                          </li>
                          <li data-id="element-2998">Handling Email Attachments Safely</li>
                          <li data-id="element-2999">Email Security Beyond Passwords</li>
                          <li data-id="element-3000">Social Media Privacy and Security</li>
                          <li data-id="element-3001">Making Your Web Browser More Private</li>
                          <li data-id="element-3002">Spring Cleaning Your Phone</li>
                          <li data-id="element-3003">Recognizing and Avoiding Financial Scams</li>
                          <li data-id="element-3004">Protecting Your Financial Accounts and Credit</li>
                          <li data-id="element-3005">Cloud Storage and Family Sharing</li>
                          <li data-id="element-3006">Advanced Privacy Alternatives</li>
                        </ul>
                      </div>

                      <div data-id="element-3007">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3008">
                          Level 3: Advanced Security Practices (8 Modules)
                        </h5>
                        <p className="mb-2 text-sm" data-id="element-3009">
                          These are proactive optimizations and advanced topics.
                          Not required for basic security, but valuable for
                          users who want to go further.
                        </p>
                        <ul className="list-disc pl-6 space-y-1" data-id="element-3010">
                          <li data-id="element-3011">Complete Password Overhaul</li>
                          <li data-id="element-3012">Creating a Digital Vault for Sensitive Files</li>
                          <li data-id="element-3013">Setting Up Secure Messaging</li>
                          <li data-id="element-3014">Setting Up Device Tracking and Remote Wipe</li>
                          <li data-id="element-3015">Travel Security and Public WiFi Safety</li>
                          <li data-id="element-3016">Understanding Encryption</li>
                          <li data-id="element-3017">Securing Your Smart Home Devices</li>
                          <li data-id="element-3018">Keeping Kids Safe Online</li>
                        </ul>
                      </div>

                      <div data-id="element-3019">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3020">
                          Specialized Guides (9 Deep Dives)
                        </h5>
                        <p className="mb-2 text-sm" data-id="element-3021">
                          These aren't sequential modules but reference guides
                          for specific situations, accessed as needed rather
                          than in order.
                        </p>
                        <ul className="list-disc pl-6 space-y-1" data-id="element-3022">
                          <li data-id="element-3023">Reducing Spam and Junk Mail</li>
                          <li data-id="element-3024">A Primer on AI and Using AI Safely</li>
                          <li data-id="element-3025">
                            Masking Your Identity for Online Dating, Events, or
                            Organizing
                          </li>
                          <li data-id="element-3026">Replacing ISP Modem and Router</li>
                          <li data-id="element-3027">Setting Up Parental Controls</li>
                          <li data-id="element-3028">Secure File Sharing for Families</li>
                          <li data-id="element-3029">Digital Legacy Planning</li>
                          <li data-id="element-3030">Smart Home Ecosystem Selection</li>
                          <li data-id="element-3031">Creating a Family Digital Safety Plan</li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 mt-6" data-id="element-3032">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3033">
                          The Learning Experience
                        </h5>
                        <p className="mb-4" data-id="element-3034">
                          Each module is built around real-world scenarios
                          families actually face. Plain English, visual
                          storytelling, and interactive elements make learning
                          feel light and engaging, never overwhelming. This
                          isn't homework. It's designed to delight. The same
                          module adapts to different users automatically.
                        </p>
                        <p className="mb-4" data-id="element-3035">
                          For a 70-year-old learning about password security,
                          the interface might say:{' '}
                          <span className="italic" data-id="element-3036">
                            "Think of your password like the key to your front
                            door. You wouldn't use the same key for your house,
                            your car, and your office, right? Let's create
                            unique passwords that are easy to remember."
                          </span>
                        </p>
                        <p data-id="element-3037">
                          For a 45-year-old tech-savvy professional, the same
                          module says:{' '}
                          <span className="italic" data-id="element-3038">
                            "Password reuse is your biggest vulnerability. We'll
                            show you how to implement a passkey strategy that
                            eliminates passwords entirely for your most critical
                            accounts."
                          </span>{' '}
                          The UI adjusts its terminology, examples, and
                          sophistication based on the user's role and
                          demonstrated literacy level, ensuring that a
                          75-year-old and a 45-year-old both get exactly what
                          they need without friction.
                        </p>
                      </div>

                      <div data-id="element-3039">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3040">
                          The Scoring System Creates Motivation
                        </h5>
                        <ul className="list-disc pl-6 space-y-1" data-id="element-3041">
                          <li data-id="element-3042">
                            Visible progress tracking: you're at 650/1000,
                            here's what you need to reach 1000
                          </li>
                          <li data-id="element-3043">
                            Family competition through shared dashboard for
                            healthy motivation
                          </li>
                          <li data-id="element-3044">
                            Clear finish line: Level 1 + Level 2 = 1000/1000 =
                            legitimate security
                          </li>
                          <li data-id="element-3045">
                            Ongoing improvement: Level 3 takes you beyond basic
                            security
                          </li>
                          <li data-id="element-3046">
                            Users can complete modules in any order (though
                            sequential is recommended), pause mid-module and
                            return later, and revisit completed modules anytime.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Feature 2 */}
                  <div data-id="element-3047">
                    <h4 className="text-lg font-bold text-[#631bb7] mb-3" data-id="element-3048">
                      Feature 2: Proactive Security Governance Activation
                    </h4>
                    <p className="text-gray-600 mb-4" data-id="element-3049">
                      Before recommending new purchases, Caveo helps families
                      activate and configure the security features already built
                      into their devices. The goal is to make security the
                      persistent default, not a reactive panic button pressed
                      during crisis moments.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-3050">
                      We guide users step-by-step to enable the protections they
                      already own but never activated:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-600 mb-6" data-id="element-3051">
                      <li data-id="element-3052">
                        Walk a Windows user through enabling BitLocker disk
                        encryption
                      </li>
                      <li data-id="element-3053">
                        Guide an iPhone user through setting up Face ID and
                        passkeys for their banking apps
                      </li>
                      <li data-id="element-3054">
                        Show an Android user how to enable Google Play Protect
                        and automatic security updates
                      </li>
                      <li data-id="element-3055">
                        Help a Mac user configure FileVault and iCloud Keychain
                      </li>
                    </ul>
                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-3056">
                      <h5 className="font-bold text-[#111111] mb-2" data-id="element-3057">
                        The Fiduciary Pledge
                      </h5>
                      <p className="text-gray-600" data-id="element-3058">
                        We maintain a pure subscription model with zero
                        affiliate revenue influence or bias. This means we can
                        provide genuinely honest guidance. If a user doesn't
                        need a standalone VPN because they already have one
                        through iCloud Private Relay or Google One, we tell
                        them. If their built-in antivirus (Windows Defender,
                        macOS XProtect) is sufficient, we say so. We don't
                        upsell products families don't need. This transparency
                        creates trust that competitors who profit from affiliate
                        commissions simply cannot offer. Note: While Caveo may
                        accept affiliate revenue for business sustainability,
                        affiliate availability never influences what we
                        recommend. Recommendation integrity is non-negotiable.
                      </p>
                    </div>
                  </div>

                  {/* Feature 3 */}
                  <div data-id="element-3059">
                    <h4 className="text-lg font-bold text-[#631bb7] mb-3" data-id="element-3060">
                      Feature 3: Ongoing Value — The Living Tech Roadmap and
                      AI-Guided Support
                    </h4>
                    <p className="text-gray-600 mb-6" data-id="element-3061">
                      Caveo provides persistent value through two mechanisms
                      that grow more valuable over time.
                    </p>

                    <div className="space-y-6" data-id="element-3062">
                      <div data-id="element-3063">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3064">
                          The Living Tech Roadmap
                        </h5>
                        <p className="text-gray-600 mb-4" data-id="element-3065">
                          A persistent, ecosystem-aware shopping guide for
                          future purchases. This turns every purchase decision
                          into an opportunity to standardize the home and reduce
                          what we call the Interoperability Tax: the hidden cost
                          of devices and services that don't work well together.
                        </p>
                        <p className="text-gray-600 mb-4" data-id="element-3066">
                          When an aging parent needs a new tablet, the roadmap
                          tells them exactly which one to buy based on the
                          family's ecosystem, so their adult child can support
                          them remotely without friction. When a professional
                          needs to upgrade their laptop, we recommend options
                          that integrate seamlessly with their existing cloud
                          storage and backup strategy.
                        </p>
                        <div className="space-y-3 text-gray-600" data-id="element-3067">
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3068">
                            <span className="font-bold text-[#111111]" data-id="element-3069">
                              Proactive Recommendations:
                            </span>{' '}
                            <span className="italic" data-id="element-3070">
                              "Your iPad is from 2019. It's approaching the
                              typical 5-6 year replacement window. Start
                              thinking about upgrades. Here's what we recommend
                              based on your family's ecosystem."
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3071">
                            <span className="font-bold text-[#111111]" data-id="element-3072">
                              Purchase Prevention:
                            </span>{' '}
                            <span className="italic" data-id="element-3073">
                              "Before you buy that Chromebook at Costco, know
                              this: Your family uses Apple devices. A Chromebook
                              won't sync with your photos or files. Consider an
                              iPad or MacBook instead."
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3074">
                            <span className="font-bold text-[#111111]" data-id="element-3075">
                              Consolidation Opportunities:
                            </span>{' '}
                            <span className="italic" data-id="element-3076">
                              "You're paying for iCloud ($0.99/month), your
                              husband pays for Google storage ($1.99/month), and
                              your parents pay for Dropbox ($9.99/month),
                              totaling $12.97/month. Switch to Apple One Family
                              plan ($22.95/month) and you'll get music, TV,
                              games, and 200GB shared storage for everyone."
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3077">
                            <span className="font-bold text-[#111111]" data-id="element-3078">
                              Built-In Feature Alerts:
                            </span>{' '}
                            <span className="italic" data-id="element-3079">
                              "You're already paying for iCloud+ which includes
                              iCloud Private Relay, a VPN-like protection. You
                              don't need to buy a separate VPN service."
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-600 mt-4" data-id="element-3080">
                          The roadmap prevents costly compatibility mistakes
                          that create hours of future tech support calls, and
                          identifies consolidation opportunities that save
                          families $500-$1,000 annually.
                        </p>
                      </div>

                      <div data-id="element-3081">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3082">
                          AI-Guided Support
                        </h5>
                        <p className="text-gray-600 mb-4" data-id="element-3083">
                          Our AI assistant provides contextual help based on the
                          user's real assessment data and family topology. The
                          personality is blunt, upfront, and honest. No
                          sugarcoating. It's a guide, not a cheerleader. The
                          tone isn't mean; it's real. Like a personal trainer
                          who genuinely wants you to succeed but won't let you
                          skip the hard stuff. The assistant has full context
                          including complete assessment data, device inventory,
                          module completion progress, family topology, and
                          shopping guide status. Examples of how it helps:
                        </p>
                        <div className="space-y-3 text-gray-600" data-id="element-3084">
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3085">
                            <span className="font-bold text-[#111111]" data-id="element-3086">
                              Troubleshooting:
                            </span>
                            <br data-id="element-3087" />
                            <span className="font-medium" data-id="element-3088">User:</span> "My WiFi
                            keeps dropping every few hours."
                            <br data-id="element-3089" />
                            <span className="font-medium" data-id="element-3090">AI:</span>{' '}
                            <span className="italic" data-id="element-3091">
                              "Let's start with the most common fix. Have you
                              unplugged your router, waited 20 seconds, and
                              plugged it back in? This solves the problem about
                              70% of the time."
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3092">
                            <span className="font-bold text-[#111111]" data-id="element-3093">
                              Module Guidance:
                            </span>
                            <br data-id="element-3094" />
                            <span className="font-medium" data-id="element-3095">User:</span> "I'm
                            overwhelmed. Where should I start?"
                            <br data-id="element-3096" />
                            <span className="font-medium" data-id="element-3097">AI:</span>{' '}
                            <span className="italic" data-id="element-3098">
                              "Based on your assessment, here's your biggest
                              risk: You reuse the same password across multiple
                              accounts, including your bank and email. Start
                              with the Password Basics module. It's 15 minutes
                              and will show you a system you can actually
                              remember."
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3099">
                            <span className="font-bold text-[#111111]" data-id="element-3100">
                              Shopping Advice:
                            </span>
                            <br data-id="element-3101" />
                            <span className="font-medium" data-id="element-3102">User:</span> "My mom
                            needs a new tablet. What should she get?"
                            <br data-id="element-3103" />
                            <span className="font-medium" data-id="element-3104">AI:</span>{' '}
                            <span className="italic" data-id="element-3105">
                              "Your mom uses an iPhone and you help her with
                              tech remotely. She should get an iPad so it syncs
                              with her phone and you can walk her through
                              problems over the phone. Specific recommendation:
                              iPad 10th generation ($349 at Apple, often $329 on
                              Amazon). Don't let her buy a Samsung tablet or
                              Chromebook even if it's cheaper; you won't be able
                              to help her with it."
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-3106">
                            <span className="font-bold text-[#111111]" data-id="element-3107">
                              Direct Talk:
                            </span>
                            <br data-id="element-3108" />
                            <span className="font-medium" data-id="element-3109">User:</span> completed
                            scam recognition module three weeks ago.
                            <br data-id="element-3110" />
                            <span className="font-medium" data-id="element-3111">AI:</span>{' '}
                            <span className="italic" data-id="element-3112">
                              "You still haven't enabled two-factor
                              authentication on your email. This is critical.
                              One in three people will experience fraud. 2FA
                              stops most account takeovers even if your password
                              is compromised. Why haven't you done this yet?"
                            </span>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-3113">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3114">
                          Human Escalation Path
                        </h5>
                        <p className="text-gray-600" data-id="element-3115">
                          When a situation requires empathy or complex judgment,
                          such as a suspected active scam, complex family
                          dynamics, or edge cases beyond the knowledge base,
                          users can escalate directly to Caveo's support team.
                          The support team has full context from the assessment
                          and can provide personalized guidance without the user
                          having to re-explain their situation.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Feature 4 */}
                  <div data-id="element-3116">
                    <h4 className="text-lg font-bold text-[#631bb7] mb-3" data-id="element-3117">
                      Feature 4: Coordination — The Family Dashboard
                    </h4>
                    <p className="text-gray-600 mb-4" data-id="element-3118">
                      A centralized hub for the Sandwich Generation IT lead to
                      monitor the household's security posture without creating
                      surveillance anxiety.
                    </p>

                    <div className="space-y-6" data-id="element-3119">
                      <div data-id="element-3120">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3121">
                          What the Dashboard Shows
                        </h5>
                        <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-3122">
                          <li data-id="element-3123">
                            <span className="font-bold text-[#111111]" data-id="element-3124">
                              Family Security Score:
                            </span>{' '}
                            Aggregate score out of 1000 based on Level 1 and
                            Level 2 module completion across all family members.
                          </li>
                          <li data-id="element-3125">
                            <span className="font-bold text-[#111111]" data-id="element-3126">
                              Individual Progress:
                            </span>{' '}
                            See which family members have completed which
                            modules, with their permission. Consent is required.
                          </li>
                          <li data-id="element-3127">
                            <span className="font-bold text-[#111111]" data-id="element-3128">
                              Household View:
                            </span>{' '}
                            For multi-household families such as adult children
                            helping parents who live separately, see the status
                            of each household's security posture.
                          </li>
                          <li data-id="element-3129">
                            <span className="font-bold text-[#111111]" data-id="element-3130">
                              Shared Shopping Guides:
                            </span>{' '}
                            When appropriate and with consent, family members
                            can view each other's recommended purchases to
                            coordinate. For example:{' '}
                            <span className="italic" data-id="element-3131">
                              "Mom's iPad is about to die. Here's what she
                              should replace it with based on our family's
                              ecosystem."
                            </span>
                          </li>
                          <li data-id="element-3132">
                            <span className="font-bold text-[#111111]" data-id="element-3133">
                              Celebration of Wins:
                            </span>{' '}
                            <span className="italic" data-id="element-3134">
                              "Your family completed 15 modules this month! Your
                              collective security score increased from 620 to
                              780."
                            </span>
                          </li>
                        </ul>
                      </div>

                      <div data-id="element-3135">
                        <h5 className="font-bold text-[#111111] mb-2" data-id="element-3136">
                          Consent-Based Visibility — No Surveillance
                        </h5>
                        <p className="text-gray-600 mb-3" data-id="element-3137">
                          The dashboard shows status, not activity. The IT lead
                          can see 'Mom's iPad hasn't been updated in 2 weeks'
                          but cannot see which websites Mom visited. They can
                          see 'Dad hasn't completed the phishing recognition
                          module' but cannot read Dad's emails.
                        </p>
                        <p className="text-gray-600 mb-3" data-id="element-3138">
                          This is consent-based coordination. Adult children
                          don't get automatic access to monitor parents;
                          permissions must be explicitly granted by each family
                          member. The goal is proactive support ('I can help you
                          update your device before it becomes a problem')
                          rather than reactive crisis management.
                        </p>
                        <p className="text-gray-600 mb-3" data-id="element-3139">
                          Every family member controls what they share.
                          Teenagers might share security scores with parents but
                          not browsing habits. Parents might share shopping
                          guides with adult children for coordinated purchasing
                          but keep individual module progress private.
                        </p>
                        <p className="text-gray-600" data-id="element-3140">
                          The family dashboard creates accountability, with
                          everyone able to see if Dad still hasn't enabled 2FA,
                          and shared progress, framing security as a family
                          project rather than one person's responsibility, while
                          fully respecting individual autonomy and privacy.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 7. Our Change Management Framework */}
          <section className="border-t border-gray-100 pt-10" data-id="element-3141">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-3142">
              Our Change Management Framework
            </h2>
            <div className="space-y-6 text-gray-600 leading-relaxed" data-id="element-3143">
              <p data-id="element-3144">
                Our approach mirrors proven enterprise IT change management
                frameworks, specifically the ADKAR model (Awareness, Desire,
                Knowledge, Ability, Reinforcement) used by Fortune 500
                companies, adapted specifically for household digital
                transformation.
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-3145">
                <li data-id="element-3146">
                  <span className="font-bold text-[#111111]" data-id="element-3147">Assess:</span>{' '}
                  Understand the current security posture and highest-risk gaps.
                </li>
                <li data-id="element-3148">
                  <span className="font-bold text-[#111111]" data-id="element-3149">Prioritize:</span>{' '}
                  Focus on high-impact, low-friction changes first (our Level 1
                  curriculum).
                </li>
                <li data-id="element-3150">
                  <span className="font-bold text-[#111111]" data-id="element-3151">Enable:</span>{' '}
                  Provide hands-on implementation support, not just
                  instructions.
                </li>
                <li data-id="element-3152">
                  <span className="font-bold text-[#111111]" data-id="element-3153">Measure:</span>{' '}
                  Track adoption and capability building with our 1000-point
                  scoring system.
                </li>
                <li data-id="element-3154">
                  <span className="font-bold text-[#111111]" data-id="element-3155">Sustain:</span>{' '}
                  Ongoing nudges and maintenance support to prevent backsliding.
                </li>
              </ul>
              <p data-id="element-3156">
                <span className="font-bold text-[#111111]" data-id="element-3157">
                  Curated Recommendations:
                </span>{' '}
                Throughout the curriculum, we surface vetted recommendations at
                the point of need, never a generic list, always tailored to each
                family's current setup and ideal future state. When built-in
                protections are sufficient, we say so explicitly and recommend
                against spending anything at all.
              </p>
              <p data-id="element-3158">
                <span className="font-bold text-[#111111]" data-id="element-3159">
                  Economic Clarity:
                </span>{' '}
                By auditing digital bloat, eliminating redundant subscriptions,
                and right-sizing technology spending, Caveo typically saves
                families $500-1,000 annually.
              </p>
            </div>
          </section>

          {/* 8. The Caveo Journey: From Vulnerable to Empowered */}
          <section className="border-t border-gray-100 pt-10" data-id="element-3160">
            <h2 className="text-2xl font-bold mb-4 text-[#631bb7]" data-id="element-3161">
              The Caveo Journey: From Vulnerable to Empowered
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-3162">
              Caveo moves families through a clear progression, from their most
              vulnerable state to becoming confident, proactive digital
              households.
            </p>
            <div className="space-y-6" data-id="element-3163">
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-3164">
                <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-3165">
                  From Vulnerable to Secure
                </h3>
                <p className="text-gray-600" data-id="element-3166">
                  Immediately addresses urgent risks: scam education, password
                  updates, and backups. Adult children are freed from constant
                  tech support. Families quickly see their biggest blind spots
                  and start closing them.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-3167">
                <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-3168">
                  From Secure to Empowered
                </h3>
                <p className="text-gray-600" data-id="element-3169">
                  Progress is tracked through the Security Score. Nudges in each
                  person's preferred format encourage ongoing learning. Families
                  see collective progress via the shared dashboard. Ongoing
                  education builds habits that reduce vulnerability over time.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-3170">
                <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-3171">
                  From Empowered to the Caveo State
                </h3>
                <p className="text-gray-600" data-id="element-3172">
                  Families shift from reactive problem-solving to proactive use
                  of technology. They save money by standardizing subscriptions
                  and devices. They gain confidence managing smart homes, apps,
                  and digital services. Technology becomes a bridge for
                  connection instead of a source of conflict.
                </p>
              </div>
            </div>
          </section>

          {/* 9. Benefits and Payoffs */}
          <section className="border-t border-gray-100 pt-10" data-id="element-3173">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-3174">
              Benefits and Payoffs
            </h2>
            <div className="space-y-8" data-id="element-3175">
              <div data-id="element-3176">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3177">
                  Immediate Wins (Why Families Buy)
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-3178">
                  <li data-id="element-3179">
                    Relief from urgent risks through scam education, password
                    fixes, and backup checklists.
                  </li>
                  <li data-id="element-3180">
                    Adult children freed from the always-on IT support role.
                  </li>
                  <li data-id="element-3181">
                    Families quickly see where their biggest blind spots are and
                    start closing them.
                  </li>
                </ul>
              </div>
              <div data-id="element-3182">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3183">
                  Guided Progress (Why Families Stay)
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-3184">
                  <li data-id="element-3185">
                    The Security Score gamifies improvement, showing visible
                    progress.
                  </li>
                  <li data-id="element-3186">
                    Nudges delivered in each person's preferred format: print,
                    video, audio, interactive.
                  </li>
                  <li data-id="element-3187">
                    The Family Dashboard shows collective progress and flags
                    overdue high-impact tasks.
                  </li>
                  <li data-id="element-3188">
                    Ongoing education builds habits that reduce vulnerability
                    over time.
                  </li>
                </ul>
              </div>
              <div data-id="element-3189">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3190">
                  Long-Term Payoff (Family Empowerment)
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-3191">
                  <li data-id="element-3192">
                    <span className="font-bold text-[#111111]" data-id="element-3193">
                      Cost Savings and Simplification:
                    </span>{' '}
                    Families standardize subscriptions and devices, reducing
                    overspending and fragmentation.
                  </li>
                  <li data-id="element-3194">
                    <span className="font-bold text-[#111111]" data-id="element-3195">
                      Better Use of Existing Tech:
                    </span>{' '}
                    From smart doorbells to app management, Caveo helps families
                    unlock more value from what they already own.
                  </li>
                  <li data-id="element-3196">
                    <span className="font-bold text-[#111111]" data-id="element-3197">
                      Confidence and Independence:
                    </span>{' '}
                    Properly configured notifications, junk mail management, and
                    secure browsing help individuals feel capable rather than
                    dependent.
                  </li>
                  <li data-id="element-3198">
                    <span className="font-bold text-[#111111]" data-id="element-3199">
                      Stronger Connections:
                    </span>{' '}
                    Shared language around digital safety turns tech into a
                    bridge rather than a barrier. Families move from conflict to
                    collaboration.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* 10. Role-Based Architecture */}
          <section className="border-t border-gray-100 pt-10" data-id="element-3200">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-3201">
              Role-Based Architecture
            </h2>

            <div className="space-y-10" data-id="element-3202">
              <div data-id="element-3203">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3204">
                  Goal-First, Role-Aware Onboarding
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3205">
                  Caveo's onboarding begins with role clarity rather than
                  technical assessment. Families approach technology from very
                  different positions of responsibility, confidence, and
                  available time. Asking 'how technical are you?' is the wrong
                  first question. Instead, Caveo starts with: 'What best
                  describes your role when it comes to managing technology and
                  digital security?'
                </p>
                <p className="text-gray-600 mb-4" data-id="element-3206">
                  By anchoring onboarding to role rather than skill level, every
                  user can enter the product without intimidation, judgment, or
                  unnecessary friction. Once a role is identified, Caveo
                  presents a short, curated set of suggested objectives tailored
                  to that role, framed as outcomes rather than features:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-3207">
                  <li data-id="element-3208">Establish a family security baseline</li>
                  <li data-id="element-3209">Reduce the number of tech emergencies</li>
                  <li data-id="element-3210">
                    Ensure critical accounts cannot be locked or compromised
                  </li>
                  <li data-id="element-3211">
                    Set up safe defaults and stop worrying about what may have
                    been missed
                  </li>
                </ul>
              </div>

              <div data-id="element-3212">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3213">
                  Core Family Roles
                </h3>
                <ul className="space-y-4 text-gray-600" data-id="element-3214">
                  <li data-id="element-3215">
                    <span className="font-bold text-[#111111]" data-id="element-3216">
                      Guardian Member:
                    </span>{' '}
                    The primary coordinator for a family, or an individual
                    managing their own account without others. Initiates the
                    account, extends and manages invitations, owns setup and
                    billing. Receives family-level insights for those who have
                    opted into sharing, and reminders. Not assumed to be a
                    technical expert.
                  </li>
                  <li data-id="element-3217">
                    <span className="font-bold text-[#111111]" data-id="element-3218">
                      Super Guardian Member:
                    </span>{' '}
                    Extends the Guardian role for situations requiring
                    additional permissions, oversight, or hands-on assistance.
                    Super Guardian access is designed to support, not override,
                    autonomy. Any user leveraging remote access to provide
                    support to a family will have this role.
                  </li>
                  <li data-id="element-3219">
                    <span className="font-bold text-[#111111]" data-id="element-3220">
                      Adult Member:
                    </span>{' '}
                    An independent adult who joins a family account by
                    invitation. Adult Members retain full ownership of their
                    account, but are encouraged to opt in to Family Sharing and
                    Support.
                  </li>
                  <li data-id="element-3221">
                    <span className="font-bold text-[#111111]" data-id="element-3222">
                      Child Member:
                    </span>{' '}
                    Designed for minors and always overseen by a Guardian. The
                    experience prioritizes safety, age-appropriate guidance, and
                    simplicity.
                  </li>
                </ul>
              </div>

              <div data-id="element-3223">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3224">
                  Governance and Autonomy Charter
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3225">
                  Caveo is built on the principle that digital safety should
                  never come at the cost of personal autonomy. While families
                  may coordinate their digital security efforts through shared
                  plans, every individual retains sovereignty over their own
                  identity, data, and participation.
                </p>
                <ul className="space-y-4 text-gray-600" data-id="element-3226">
                  <li data-id="element-3227">
                    <span className="font-bold text-[#111111]" data-id="element-3228">
                      Account Sovereignty:
                    </span>{' '}
                    Every Adult Member owns their personal account and data,
                    even within a family plan. Membership in a family plan does
                    not transfer ownership of login credentials, learning
                    history, personal progress data, or connected services. An
                    Adult Member may leave a family plan at any time and convert
                    to an independent account without losing their history or
                    progress. Billing authority does not equal identity
                    authority.
                  </li>
                  <li data-id="element-3229">
                    <span className="font-bold text-[#111111]" data-id="element-3230">
                      Consent-Based Support:
                    </span>{' '}
                    All elevated access, including Super Guardian support and
                    Remote Assist, is explicitly consent-based. Support
                    permissions must be granted by the supported member, clearly
                    scoped, revocable at any time, session-based for live
                    access, and logged for audit. Caveo does not enable silent
                    monitoring or background surveillance between family
                    members.
                  </li>
                  <li data-id="element-3231">
                    <span className="font-bold text-[#111111]" data-id="element-3232">
                      Billing vs. Feature Access:
                    </span>{' '}
                    Guardians manage subscription billing. Downgrading a member
                    affects feature access, not ownership of their identity or
                    data. No member can be locked out by another.
                  </li>
                  <li data-id="element-3233">
                    <span className="font-bold text-[#111111]" data-id="element-3234">
                      Transparent Visibility:
                    </span>{' '}
                    Family dashboards provide high-level, opt-in progress
                    indicators designed for encouragement, not control. Caveo is
                    not parental control software for adults.
                  </li>
                  <li data-id="element-3235">
                    <span className="font-bold text-[#111111]" data-id="element-3236">
                      Exit Without Penalty:
                    </span>{' '}
                    Any Adult Member can leave a family plan, convert to an
                    individual account, and retain all their progress and
                    history. Family conflict does not erase personal digital
                    capability.
                  </li>
                </ul>
              </div>

              <div data-id="element-3237">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3238">
                  When Caveo Acts as Guardian
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3239">
                  Not every family has a designated tech-savvy member to
                  coordinate digital security, and not every individual wants to
                  assume that responsibility. When users indicate that no Family
                  IT Person exists and they do not wish to take on that role
                  themselves, Caveo fills the coordination gap by design.
                </p>
                <p className="text-gray-600 mb-4" data-id="element-3240">
                  In these situations, the platform shifts into an automated
                  stewardship mode. This does not mean surveillance or expanded
                  access. It means Caveo assumes the orchestration
                  responsibilities that would otherwise fall to a human
                  Guardian. Automated stewardship includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-4" data-id="element-3241">
                  <li data-id="element-3242">
                    Structuring and sequencing a personalized learning path
                  </li>
                  <li data-id="element-3243">Surfacing the right modules at the right time</li>
                  <li data-id="element-3244">Delivering proactive nudges and reinforcement</li>
                  <li data-id="element-3245">
                    Tracking progress and strengthening habits through spaced
                    repetition
                  </li>
                  <li data-id="element-3246">
                    Offering opt-in simulated phishing and smishing exercises
                  </li>
                  <li data-id="element-3247">
                    Guiding users through self-assessments to identify and
                    resolve common misconfigurations
                  </li>
                </ul>
                <p className="text-gray-600" data-id="element-3248">
                  Caveo acts as a curriculum coordinator and behavioral
                  reinforcement engine, not a monitor. It does not access
                  private communications, take device-level control, or operate
                  in the background without consent. All actions remain
                  user-initiated and transparent. This matters most for
                  independent seniors, senior couples, and self-sufficient
                  individuals who value autonomy but lack a dedicated support
                  person.
                </p>
              </div>
            </div>
          </section>

          {/* 11. Teaching, Personalization, and Trust */}
          <section className="border-t border-gray-100 pt-10" data-id="element-3249">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-3250">
              Teaching, Personalization, and Trust
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-3251">
              Caveo's differentiation is not just what we teach, but how we
              teach and why people trust us. The experience is structured to
              reduce overwhelm and guide families step by step. It should feel
              less like cybersecurity training and more like a personal coach:
              approachable, practical, and designed for real family life.
            </p>

            <div className="space-y-10" data-id="element-3252">
              <div data-id="element-3253">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3254">
                  The Learning Science Behind Caveo
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3255">
                  Our learning methodology is grounded in research from
                  cognitive psychology (Baddeley's working memory model), adult
                  learning theory (Knowles' andragogy), and behavioral economics
                  (Thaler's nudge theory). Traditional security training fails
                  because it is passive and fear-based. Caveo's approach is
                  built on proven principles:
                </p>
                <ul className="space-y-4 text-gray-600" data-id="element-3256">
                  <li data-id="element-3257">
                    <span className="font-bold text-[#111111]" data-id="element-3258">
                      Active, Not Passive:
                    </span>{' '}
                    People retain information when they participate, not when
                    they are lectured. Every Caveo module includes interactive
                    elements: checklists to complete, scenarios to work through,
                    practice tasks that build confidence.
                  </li>
                  <li data-id="element-3259">
                    <span className="font-bold text-[#111111]" data-id="element-3260">
                      Emotionally Safe:
                    </span>{' '}
                    Fear and shame shut down learning. Caveo maintains a
                    supportive, family-friendly tone. Errors are framed as
                    learning opportunities. Progress is celebrated with positive
                    reinforcement, never scolding.
                  </li>
                  <li data-id="element-3261">
                    <span className="font-bold text-[#111111]" data-id="element-3262">
                      Multimodal by Design:
                    </span>{' '}
                    People learn differently. Caveo delivers every concept
                    through visual, kinesthetic, reading/writing, and optional
                    auditory channels. Every module includes at least two
                    modalities.
                  </li>
                  <li data-id="element-3263">
                    <span className="font-bold text-[#111111]" data-id="element-3264">
                      Bite-Sized and Relevant:
                    </span>{' '}
                    Working memory is limited. Caveo breaks learning into 2-4
                    minute micro-lessons with one key idea per screen, tied to
                    relatable, real-world scenarios.
                  </li>
                  <li data-id="element-3265">
                    <span className="font-bold text-[#111111]" data-id="element-3266">
                      Spaced Repetition:
                    </span>{' '}
                    Memory strengthens through retrieval over time. Caveo uses
                    gentle reminders to prompt recall of key concepts; not
                    nagging, but strategic reinforcement.
                  </li>
                  <li data-id="element-3267">
                    <span className="font-bold text-[#111111]" data-id="element-3268">
                      Relatable Analogies:
                    </span>{' '}
                    Abstract security concepts are connected to everyday life.
                    For example: leaving your key under the mat, or why you
                    wouldn't use the same key for every lock you own.
                  </li>
                  <li data-id="element-3269">
                    <span className="font-bold text-[#111111]" data-id="element-3270">
                      Progress at Your Pace:
                    </span>{' '}
                    Every person's learning journey moves at their own pace, and
                    that is by design. Caveo never sets timelines, never
                    pressures users to advance faster than is natural for them,
                    and never treats slower progress as a problem. The goal is
                    durable capability, not fast completion.
                  </li>
                </ul>
              </div>

              <div data-id="element-3271">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3272">
                  Universal Accessibility: WCAG 2.2 AA Compliant
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3273">
                  Caveo is designed for everyone, including older adults and
                  those with cognitive or physical differences. Accessibility is
                  not an add-on; it is foundational to serving
                  multi-generational families. Specific standards include:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-3274">
                  <li data-id="element-3275">Minimum 16pt scalable text</li>
                  <li data-id="element-3276">4.5:1 color contrast ratios</li>
                  <li data-id="element-3277">44x44px touch targets</li>
                  <li data-id="element-3278">Full keyboard and screen reader compatibility</li>
                  <li data-id="element-3279">
                    Captions and transcripts for all audio and video content
                  </li>
                  <li data-id="element-3280">Supportive error handling with undo options</li>
                </ul>
              </div>

              <div data-id="element-3281">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3282">
                  Portable Learning for Real Life
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3283">
                  Digital security does not happen just on laptops. Caveo makes
                  every lesson portable:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-4" data-id="element-3284">
                  <li data-id="element-3285">
                    <span className="font-bold text-[#111111]" data-id="element-3286">Print:</span>{' '}
                    Take a checklist to your router
                  </li>
                  <li data-id="element-3287">
                    <span className="font-bold text-[#111111]" data-id="element-3288">Email/SMS:</span>{' '}
                    Send instructions to yourself or family members
                  </li>
                  <li data-id="element-3289">
                    <span className="font-bold text-[#111111]" data-id="element-3290">Copy link:</span>{' '}
                    Share a specific guide
                  </li>
                  <li data-id="element-3291">
                    <span className="font-bold text-[#111111]" data-id="element-3292">QR code:</span>{' '}
                    Quick access from mobile devices
                  </li>
                </ul>
                <p className="text-gray-600" data-id="element-3293">
                  Users can learn on one device and act on another, or share
                  guidance with family members who need it.
                </p>
              </div>

              <div data-id="element-3294">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3295">
                  AI as the Simplicity Engine
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3296">
                  The integration of AI is not merely a feature enhancement for
                  threat detection; it is the fundamental technological enabler
                  for solving the multi-generational usability crisis. AI and
                  machine learning are essential for processing the massive
                  volumes of data generated by modern homes in real time,
                  allowing security platforms to move beyond slow, reactive
                  alerts to proactive, predictive behavior modeling. Caveo uses
                  AI as the Simplicity Engine, applying it specifically to:
                </p>
                <ul className="space-y-4 text-gray-600 mb-4" data-id="element-3297">
                  <li data-id="element-3298">
                    <span className="font-bold text-[#111111]" data-id="element-3299">
                      Automated Setup and Configuration:
                    </span>{' '}
                    Eliminating the requirement for expert knowledge during
                    installation and initial security setup. This is especially
                    critical for password managers, where Caveo can guide users
                    through setup, browser integration, and family sharing in a
                    way that existing solutions simply don't.
                  </li>
                  <li data-id="element-3300">
                    <span className="font-bold text-[#111111]" data-id="element-3301">
                      Proactive Misconfiguration Identification:
                    </span>{' '}
                    Automatically flagging and providing simple, one-click fixes
                    for weak privacy settings or insecure smart home device
                    configurations.
                  </li>
                  <li data-id="element-3302">
                    <span className="font-bold text-[#111111]" data-id="element-3303">
                      Adaptive Interface Design:
                    </span>{' '}
                    If the system identifies a user who needs a simplified
                    experience based on role selection or behavioral patterns,
                    the interface automatically adjusts: minimizing
                    configuration options, using larger touch targets, and
                    prioritizing guidance prompts over complex settings menus.
                  </li>
                  <li data-id="element-3304">
                    <span className="font-bold text-[#111111]" data-id="element-3305">
                      Predictive Risk Alerting:
                    </span>{' '}
                    Leveraging AI to transition from reactive monitoring to
                    building a trusted, predictive, and secure digital
                    ecosystem. Predictive analytics allow the platform to
                    customize user experiences and proactively alert users
                    before damage occurs.
                  </li>
                </ul>
                <p className="text-gray-600" data-id="element-3306">
                  Crucially, the investment in AI-driven simplification is a
                  necessary pre-condition for monetizing the high-growth Managed
                  Services component. By automating tier 1 and 2 support issues,
                  Caveo can significantly reduce operational costs and dedicate
                  human expertise to high-value, complex family issues that
                  command premium pricing.
                </p>
              </div>
            </div>
          </section>

          {/* 12. Vision: From Digital Safety to Family Tech Hub */}
          <section className="border-t border-gray-100 pt-10" data-id="element-3307">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-3308">
              Vision: From Digital Safety to Family Tech Hub
            </h2>

            <div className="space-y-10" data-id="element-3309">
              <div data-id="element-3310">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3311">
                  The Near-Term Role: Trusted Security Partner
                </h3>
                <p className="text-gray-600" data-id="element-3312">
                  Caveo becomes the go-to family guide for digital safety. We
                  instill confidence through personalized plans, approachable
                  language, and proactive education. Families gain relief from
                  urgent risks and clarity on what matters most.
                </p>
              </div>

              <div data-id="element-3313">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3314">
                  The Long-Term Role: Family Tech Hub
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3315">
                  Caveo's entry point is digital safety, helping families
                  prevent scams, fraud, and misuse of technology. But our bigger
                  mission is empowerment. Once families trust Caveo to keep them
                  safe, they naturally turn to us for broader support.
                </p>
                <p className="text-gray-600 mb-4" data-id="element-3316">
                  Caveo evolves into the central platform where families manage
                  all aspects of technology together. Beyond scams and
                  passwords, we will support smart home troubleshooting,
                  subscription management, software migrations, and device
                  setup. Future product capabilities on the roadmap include:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-4" data-id="element-3317">
                  <li data-id="element-3318">
                    Remote login for adult children who don't live close to
                    their parents or grandparents
                  </li>
                  <li data-id="element-3319">
                    Single sign-on for products and services shared across the
                    family
                  </li>
                  <li data-id="element-3320">
                    Product recommendations and guidance on what to avoid for
                    hardware including routers, modems, smart devices, and
                    backup drives
                  </li>
                  <li data-id="element-3321">
                    Device recommendations across laptops, desktops, phones, and
                    tablets
                  </li>
                  <li data-id="element-3322">
                    Software recommendations covering identity monitoring,
                    browsers, and VPNs
                  </li>
                </ul>
                <p className="text-sm text-gray-500 italic" data-id="element-3323">
                  Note: Future product items above are roadmap-stage
                  capabilities, not current MVP features. See Section 3: Product
                  Roadmap for phasing and timelines.
                </p>
              </div>

              <div data-id="element-3324">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-3325">
                  From One Person's Burden to a Family Commitment
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3326">
                  Today, digital security in most households rests on the
                  shoulders of one person, usually the most tech-comfortable
                  family member, who has silently inherited the role without
                  asking for it. Caveo's long-term vision is to change that
                  dynamic entirely.
                </p>
                <p className="text-gray-600 mb-4" data-id="element-3327">
                  As families progress through the platform together, security
                  stops being a solo responsibility and becomes a shared family
                  value. A teenager who learns to spot a phishing link protects
                  the whole household. A senior who sets up two-factor
                  authentication on her accounts reduces risk for everyone
                  connected to her.
                </p>
                <p className="text-gray-600" data-id="element-3328">
                  Over time, Caveo cultivates a household culture where digital
                  safety is as natural and collaborative as locking the front
                  door. Not a technical burden, but a shared act of caring for
                  one another.
                </p>
              </div>

              <div className="bg-[#1a0647] text-white p-8 rounded-2xl shadow-lg relative overflow-hidden" data-id="element-3329">
                <div className="relative z-10" data-id="element-3330">
                  <h3 className="text-2xl font-bold mb-6 text-white" data-id="element-3331">
                    The Endgame: Technology as a Source of Connection
                  </h3>
                  <p className="text-lg leading-relaxed text-gray-200 mb-6" data-id="element-3332">
                    Our vision is to flip the family's relationship with
                    technology:
                  </p>
                  <ul className="space-y-4 text-lg font-medium text-white" data-id="element-3333">
                    <li className="flex items-center" data-id="element-3334">
                      <span className="text-[#00f4f8] mr-3" data-id="element-3335">→</span>
                      From conflict to collaboration.
                    </li>
                    <li className="flex items-center" data-id="element-3336">
                      <span className="text-[#00f4f8] mr-3" data-id="element-3337">→</span>
                      From fragmentation to unity.
                    </li>
                    <li className="flex items-center" data-id="element-3338">
                      <span className="text-[#00f4f8] mr-3" data-id="element-3339">→</span>
                      From fear to empowerment.
                    </li>
                  </ul>
                  <p className="text-lg leading-relaxed text-gray-200 mt-8" data-id="element-3340">
                    By making security and technology approachable for all ages,
                    Caveo helps families protect what matters most and use
                    technology to live better.
                  </p>
                </div>
                {/* Abstract background decoration */}
                <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-[#631bb7] rounded-full opacity-20 blur-3xl" data-id="element-3341"></div>
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-[#00f4f8] rounded-full opacity-10 blur-3xl" data-id="element-3342"></div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-12" data-id="element-3343">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-500" data-id="element-3344">
          <p data-id="element-3345">&copy; 2026 AppEmbrace, Inc. DBA Caveo. All rights reserved.</p>
          <p className="mt-2 text-xs text-gray-400" data-id="element-3346">
            This page contains confidential information intended only for
            authorized investors. Do not share or distribute without permission.
          </p>
        </div>
      </footer>
    </div>;
}